import type { ErrorEvent } from '@sentry/react';

import { isExpectedRefusalReport } from './monitoring/expectedRefusal';
import { isIgnoredEvent, type EnhancedEvent } from './monitoring/utils';

const SENTRY_ENVIRONMENT = process.env.SENTRY_ENVIRONMENT ?? process.env.NEXT_PUBLIC_SENTRY_ENV;
const SENTRY_RELEASE = process.env.SENTRY_RELEASE;
const NODE_ENV = process.env.NODE_ENV;

/**
 * Entry points of browser-extension content scripts injected into the page. They belong to no
 * Sortlist bundle, but the exceptions they throw surface as unhandled rejections attributed to
 * the app (reading 'M_ID' on undefined, JSON.stringify on a DOM node carrying a React fiber).
 * Anchored on the injected-script URL shape so a Sortlist asset path holding the same segment is
 * never suppressed.
 */
export const INJECTED_SCRIPT_URLS = [
  /\/injectScript\.js/i,
  /^app:\/\/\/executors\//i,
  /^app:\/\/\/(?!_next\/)[^/]+\/[^/.]+$/i,
  /^app:\/\/\/analytics\/\d+\/\d+\.js$/i,
];

export const IGNORED_CLIENT_ERRORS = [
  /**
   * @link https://github.com/WICG/ResizeObserver/issues/38#issuecomment-422126006,
   * @link https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded/50387233#50387233
   */
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Not found',
  "SyntaxError: Failed to execute 'measure' on 'Performance': The mark 'beforeRender' does not exist.",
  "This page has been deleted and it's not accessible anymore.",
  "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
  'Non-Error exception captured',
  'Non-Error promise rejection captured',
  "Invalid character: '#'",
  'Abort fetching component for route',
  'Unexpected private name',
  "Cannot read properties of undefined (reading 'domInteractive')",
  'No identifiers allowed directly after numeric literal',
  "SyntaxError: Unexpected token '('",
  'jQuery is not defined',
  '$ is not defined',
  'googletag is not defined',
  'AviviD is not defined',
  /_.+ is not defined/,
  /The node before which the new node is to be inserted is not a child of this node/,
  /The node to be removed is not a child of this node/,
  "TypeError: undefined is not an object (evaluating 'a.J')",
  'this.shouldEnforce is not a function',
  /Invariant: attempted to hard navigate to the same URL/i,
  /.+document.elementFromPoint.+/i,
  "Cannot read properties of undefined (reading 'response')",
  /Network Error/i,
  /Fetch Error/i,
  /Request aborted/i,
  /XHR Error/i,
  /adsbygoogle/i,
  /Failed to fetch/i,
  /Load failed/i,
  /xbrowser/i,
  /swbrowser/i,
  /zaloJSV2/i,
  /WebViewJavascriptBridge/i,
  /MetaMask/i,
  /window\.ton/i,
  /postMessage.*Java object is gone/i,
  /Error invoking .*Java object is gone/i,
  /SiteScript Messaging/i,
  /Origin not allowed/i,
  /Cancel rendering route/i,
  /Verification timed out/i,
  /Target container is not a DOM/i,
  /Cannot read properties of (?:null|undefined).*document/i,
  "Cannot read properties of undefined (reading 'M_ID')",
];

interface SentryClientSdk {
  init: (options: Record<string, unknown>) => void;
  captureException: (exception: unknown, captureContext?: Record<string, unknown>) => string;
}

export function createInitClient(sdk: SentryClientSdk) {
  return (sentryAppDsn: string, configOverride: Record<string, unknown> = {}) => {
    sdk.init({
      dsn: sentryAppDsn,
      tracesSampleRate: 0.005,
      maxBreadcrumbs: 50,
      normalizeDepth: 5,
      attachStacktrace: true,
      ignoreErrors: IGNORED_CLIENT_ERRORS,
      enableLogs: NODE_ENV === 'production',
      denyUrls: [
        /https:\/\/widget.intercom.io\/.*/,
        /https:\/\/www.google-analytics.com\/.*/,
        /https:\/\/www.google.com\/ads\/.*/,
        /https:\/\/js.intercomcdn.com\/.*/,
        /https:\/\/api-iam.intercom.io\/.*/,
        /https:\/\/api.hubapi.com\/.*/,
        /https:\/\/www.facebook.com\/.*/,
        /https:\/\/connect.facebook.net\/.*/,
        /https:\/\/in.hotjar.com\/.*/,
        /https:\/\/www.clarity.ms\/.*/,
        /extensions\//i,
        ...INJECTED_SCRIPT_URLS,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
      ],
      debug: NODE_ENV !== 'production',
      beforeSend(event: ErrorEvent, hint?: { originalException?: unknown; captureContext?: unknown }) {
        try {
          if (isIgnoredEvent(event as EnhancedEvent) || isExpectedRefusalReport(hint)) {
            return null;
          }
        } catch (e) {
          sdk.captureException(new Error(`isIgnoredEvent failed: ${(e as unknown as Error)?.message ?? e}`), {
            extra: { originalEvent: event },
            tags: { source: 'beforeSend' },
          });
        }

        if (NODE_ENV !== 'production') {
          return null;
        }

        return event;
      },
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE,
      ...configOverride,
    });
  };
}
